import React, { useState, useEffect } from 'react';
import {
    Calendar, Users, Zap, Crown, AlertTriangle, Save,
    RefreshCw, Shield, Unlock, CheckCircle, XCircle, Terminal, Cpu
} from 'lucide-react';
import { Input } from '@/components/common/ui/Input';
import { useGetOrganizationDetailsQuery, useUpdateOrganizationDetailsMutation } from '@/lib/services/organizationApi';
import { useToast } from '@/hooks/components/common/toast/useToast';

const OrganizationDetails: React.FC = () => {
    const [organizationName, setOrganizationName] = useState('');
    const [originalName, setOriginalName] = useState('');
    const [hasChanges, setHasChanges] = useState(false);
    const toast = useToast();

    const {
        data: orgDetailsResponse,
        isLoading: isLoadingDetails,
        isError: isDetailsError,
        refetch: refetchDetails
    } = useGetOrganizationDetailsQuery();

    const [updateOrganization, { isLoading: isUpdating }] = useUpdateOrganizationDetailsMutation();

    const orgDetails = orgDetailsResponse?.data;

    useEffect(() => {
        if (orgDetails) {
            setOrganizationName(orgDetails.name);
            setOriginalName(orgDetails.name);
        }
    }, [orgDetails]);

    useEffect(() => {
        setHasChanges(organizationName !== originalName && organizationName.trim() !== '');
    }, [organizationName, originalName]);

    const handleSave = async () => {
        if (!hasChanges || organizationName.trim() === '') return;
        try {
            await updateOrganization({ name: organizationName.trim() }).unwrap();
            setOriginalName(organizationName.trim());
            setHasChanges(false);
            toast({ type: 'success', title: 'SYSTEM_UPDATED', message: 'Registry name updated successfully.' });
        } catch (error) {
            toast({ type: 'error', title: 'WRITE_FAILURE', message: 'Failed to update organization records.' });
        }
    };

    const daysUntilExpiry = orgDetails?.license ?
        Math.ceil((new Date(orgDetails.license.expiresAt).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)) : 0;

    if (isLoadingDetails) {
        return (
            <div className="p-8 space-y-8 font-mono">
                <div className="flex items-center gap-3">
                    <RefreshCw className="h-5 w-5 animate-spin text-[#00FFFF]" />
                    <span className="text-[10px] uppercase tracking-[0.3em] text-slate-500">Retrieving_System_Manifest...</span>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {[...Array(4)].map((_, i) => (
                        <div key={i} className="h-48 bg-[#050505] border border-[#1A1D24] rounded-xl animate-pulse" />
                    ))}
                </div>
            </div>
        );
    }

    if (isDetailsError) {
        return (
            <div className="p-8 font-mono">
                <div className="bg-[#FF3333]/5 border border-[#FF3333]/20 rounded-xl p-8 text-center space-y-4">
                    <AlertTriangle className="h-10 w-10 text-[#FF3333] mx-auto" />
                    <h3 className="text-white font-bold uppercase tracking-widest">Manifest_Access_Denied</h3>
                    <p className="text-[11px] text-slate-500 uppercase">Unable to establish connection to organization metadata service.</p>
                    <button onClick={() => refetchDetails()} className="px-6 py-2 bg-transparent border border-[#FF3333] text-[#FF3333] hover:bg-[#FF3333] hover:text-white transition-all text-[10px] font-black uppercase tracking-widest rounded-lg">
                        Retry_Handshake
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="p-8 space-y-8 font-mono animate-in fade-in duration-500">
            {/* Component Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <Cpu className="h-5 w-5 text-[#00FFFF]" />
                    <h3 className="text-[14px] font-bold text-white uppercase tracking-[0.2em]">
                        Organization Details
                    </h3>
                </div>

                <button
                    onClick={handleSave}
                    disabled={!hasChanges || isUpdating}
                    className={`flex items-center gap-2 px-6 py-2.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${hasChanges
                        ? 'bg-[#50A970] text-black shadow-[0_0_15px_rgba(80,169,112,0.4)] hover:scale-105 active:scale-95'
                        : 'bg-[#1A1D24] text-slate-600 cursor-not-allowed opacity-50'
                        }`}
                >
                    {isUpdating ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                    {isUpdating ? 'Executing_Write...' : 'Commit_Changes'}
                </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* --- Left Column --- */}
                <div className="space-y-8">
                    {/* Identity Module */}
                    <div className="bg-[#050505] border border-[#1A1D24] rounded-xl p-6 relative overflow-hidden group hover:border-slate-700 transition-colors shadow-inner">
                        <div className="flex items-center gap-3 mb-6">
                            <Terminal className="h-4 w-4 text-slate-500" />
                            <h4 className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">Organization Information</h4>
                        </div>

                        <div className="space-y-6">
                            <div className="space-y-2">
                                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest ml-1">Entity_Designation</label>
                                <Input
                                    type="text"
                                    value={organizationName}
                                    onChange={(e) => setOrganizationName(e.target.value)}
                                    className="bg-black border-[#1A1D24] text-[#00FFFF] h-11 text-[12px] uppercase tracking-wider focus:border-[#00FFFF]"
                                    placeholder="Enter identifier..."
                                />
                            </div>

                            <div className="space-y-2">
                                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest ml-1">Organization ID</label>
                                <div className="px-4 py-3 bg-black border border-[#1A1D24] rounded-lg shadow-inner">
                                    <code className="text-[11px] text-[#50A970] font-bold">{orgDetails?.id}</code>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* License Authorization */}
                    <div className="bg-[#050505] border border-[#1A1D24] rounded-xl p-6 shadow-inner">
                        <div className="flex items-center gap-3 mb-6">
                            <Crown className="h-4 w-4 text-[#EAB308]" />
                            <h4 className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">License Information</h4>
                        </div>

                        <div className="space-y-5">
                            <div className="flex justify-between items-center py-2 border-b border-[#1A1D24]">
                                <span className="text-[10px] text-slate-500 uppercase tracking-widest">Protocol_Level</span>
                                <span className="text-[12px] font-bold text-white uppercase tracking-tighter">{orgDetails?.license.type}</span>
                            </div>

                            <div className="flex justify-between items-start pt-2">
                                <span className="text-[10px] text-slate-500 uppercase tracking-widest mt-1">Expiry_Timestamp</span>
                                <div className="text-right space-y-1">
                                    <span className="text-[12px] font-bold text-white uppercase">
                                        {orgDetails?.license.expiresAt ? new Date(orgDetails.license.expiresAt).toLocaleDateString() : 'N/A'}
                                    </span>
                                    <div className={`flex items-center justify-end gap-1.5 text-[9px] font-bold uppercase tracking-widest ${daysUntilExpiry < 30 ? 'text-[#FF3333]' : 'text-[#50A970]'}`}>
                                        <Calendar className="h-3 w-3" />
                                        {daysUntilExpiry} Cycles Remaining
                                    </div>
                                </div>
                            </div>

                            {daysUntilExpiry < 30 && (
                                <div className="bg-[#FF3333]/5 border border-[#FF3333]/20 rounded-lg p-4 flex items-start gap-3 mt-4">
                                    <AlertTriangle className="h-4 w-4 text-[#FF3333] flex-shrink-0 mt-0.5" />
                                    <div className="space-y-1">
                                        <p className="text-[10px] font-bold text-[#FF3333] uppercase">Signal_Weakening</p>
                                        <p className="text-[9px] text-slate-500 uppercase leading-relaxed">License expiration imminent. Renew authorization to prevent terminal lock.</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* --- Right Column --- */}
                <div className="space-y-8">
                    {/* API Telemetry */}
                    <div className="bg-[#050505] border border-[#1A1D24] rounded-xl p-6 shadow-inner">
                        <div className="flex items-center gap-3 mb-6">
                            <Zap className="h-4 w-4 text-[#00FFFF]" />
                            <h4 className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">API Usage</h4>
                        </div>

                        <div className="space-y-4">
                            <div className="flex justify-between items-center">
                                <span className="text-[10px] text-slate-500 uppercase tracking-widest">Monthly_Limit</span>
                                <span className="text-[12px] font-bold text-white uppercase tracking-tighter">
                                    {orgDetails?.apiUsage.isUnlimited ? 'UNLIMITED_EXFIL' : orgDetails?.apiUsage.monthlyLimit.toLocaleString()}
                                </span>
                            </div>

                            {!orgDetails?.apiUsage.isUnlimited && (
                                <div className="space-y-3 pt-2">
                                    <div className="flex justify-between items-end">
                                        <span className="text-[9px] font-bold text-slate-600 uppercase tracking-widest">Buffer_Utilization</span>
                                        <span className="text-[10px] font-bold text-[#00FFFF] tracking-tighter">
                                            {orgDetails?.apiUsage.currentUsage.toLocaleString()} / {orgDetails?.apiUsage.monthlyLimit.toLocaleString()}
                                        </span>
                                    </div>
                                    <div className="w-full bg-[#1A1D24] rounded-full h-1.5 overflow-hidden">
                                        <div
                                            className="bg-[#00FFFF] h-full shadow-[0_0_8px_#00FFFF] transition-all duration-1000"
                                            style={{ width: `${Math.min(orgDetails?.apiUsage.usagePercentage || 0, 100)}%` }}
                                        />
                                    </div>
                                    <div className="text-[9px] text-slate-600 uppercase tracking-[0.15em] flex justify-between">
                                        <span>Load: {orgDetails?.apiUsage.usagePercentage.toFixed(1)}%</span>
                                        <span>Reset: {orgDetails?.apiUsage.resetDate ? new Date(orgDetails.apiUsage.resetDate).toLocaleDateString() : 'Monthly'}</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* User Slots & Unmasking */}
                    <div className="grid grid-cols-1 gap-6">
                        {/* Users Card */}
                        <div className="bg-[#050505] border border-[#1A1D24] rounded-xl p-6 shadow-inner">
                            <div className="flex items-center justify-between mb-6">
                                <div className="flex items-center gap-3">
                                    <Users className="h-4 w-4 text-slate-500" />
                                    <h4 className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">Seats</h4>
                                </div>
                                <span className="text-[12px] font-bold text-white uppercase tracking-tighter">
                                    {orgDetails?.seats.used} / {orgDetails?.seats.total}
                                </span>
                            </div>
                            <div className="w-full bg-[#1A1D24] rounded-full h-1.5 overflow-hidden mb-3">
                                <div
                                    className="bg-[#50A970] h-full shadow-[0_0_8px_#50A970] transition-all"
                                    style={{ width: `${Math.min(orgDetails?.seats.usagePercentage || 0, 100)}%` }}
                                />
                            </div>
                            <div className="text-[9px] text-slate-600 uppercase tracking-widest">
                                {orgDetails?.seats.available} Slots available for recruitment
                            </div>
                        </div>

                        {/* Unmasking Card */}
                        <div className="bg-[#050505] border border-[#1A1D24] rounded-xl p-6 shadow-inner">
                            <div className="flex items-center justify-between mb-6">
                                <div className="flex items-center gap-3">
                                    <Unlock className="h-4 w-4 text-[#A855F7]" />
                                    <h4 className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">Unmasking Usage</h4>
                                </div>
                                <span className="text-[12px] font-bold text-white uppercase tracking-tighter">
                                    {orgDetails?.planDetails.unmasking.isUnlimited ? 'INF' : orgDetails?.planDetails.unmasking.monthlyLimit.toLocaleString()}
                                </span>
                            </div>
                            {!orgDetails?.planDetails.unmasking.isUnlimited && (
                                <>
                                    <div className="w-full bg-[#1A1D24] rounded-full h-1.5 overflow-hidden mb-3">
                                        <div
                                            className="bg-[#A855F7] h-full shadow-[0_0_8px_#A855F7] transition-all"
                                            style={{ width: `${Math.min(((orgDetails?.planDetails.unmasking.used || 0) / (orgDetails?.planDetails.unmasking.monthlyLimit || 1)) * 100, 100)}%` }}
                                        />
                                    </div>
                                    <div className="text-[9px] text-slate-600 uppercase tracking-widest">
                                        {orgDetails?.planDetails.unmasking.remaining} Identifiers remaining in current cycle
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Matrix Features */}
            <div className="bg-[#050505] border border-[#1A1D24] rounded-xl p-8 shadow-inner relative overflow-hidden">
                <div className="flex items-center gap-3 mb-8">
                    <Shield className="h-5 w-5 text-[#50A970]" />
                    <h4 className="text-[12px] font-bold text-white uppercase tracking-[0.2em]">
                        Plan Features
                    </h4>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {orgDetails?.planDetails.features && Object.entries(orgDetails.planDetails.features).map(([feature, enabled]) => (
                        <div key={feature} className={`flex items-center gap-4 p-4 rounded-xl border transition-all duration-300 ${enabled ? 'bg-black border-[#50A970]/20' : 'bg-transparent border-[#1A1D24] opacity-40'
                            }`}>
                            {enabled ? (
                                <CheckCircle className="h-4 w-4 text-[#50A970] drop-shadow-[0_0_5px_rgba(80,169,112,0.5)] flex-shrink-0" />
                            ) : (
                                <XCircle className="h-4 w-4 text-slate-700 flex-shrink-0" />
                            )}
                            <span className={`text-[11px] font-bold uppercase tracking-widest ${enabled ? 'text-white' : 'text-slate-600'}`}>
                                {feature.replace(/([A-Z])/g, ' $1').trim().replace(' ', '_')}
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default OrganizationDetails;